// src/features/tasks/hooks/useTasks.js
import { useState, useCallback } from 'react';
import { tasksAPI } from '../services/tasks';
import { useApi } from '../../../hooks/api/useApi';

export function useTasks() {
  const [tasks, setTasks] = useState([]);
  const [currentTask, setCurrentTask] = useState(null);
  const [taskStatistics, setTaskStatistics] = useState(null);
  const { loading, error, execute, clearError } = useApi();

  // Task CRUD operations
  const createTask = useCallback(async (data) => {
    const result = await execute(() => tasksAPI.createTask(data));
    if (result.success) {
      // Optionally refresh tasks
      await fetchDepartmentTasks();
    }
    return result;
  }, [execute]);

  const fetchDepartmentTasks = useCallback(async (params = {}) => {
    const result = await execute(() => tasksAPI.getDepartmentTasks(params));
    if (result.success) {
      setTasks(result.data.data?.tasks || []);
    }
    return result;
  }, [execute]);

  const fetchMyTasks = useCallback(async (params = {}) => {
    const result = await execute(() => tasksAPI.getMyTasks(params));
    if (result.success) {
      setTasks(result.data.data?.tasks || []);
    }
    return result;
  }, [execute]);

  const fetchAllMyTasks = useCallback(async () => {
    return await execute(() => tasksAPI.getAllMyTasks());
  }, [execute]);

  const fetchMyTodaysTasks = useCallback(async () => {
    return await execute(() => tasksAPI.getMyTodaysTasks());
  }, [execute]);

  const fetchTaskById = useCallback(async (taskId) => {
    const result = await execute(() => tasksAPI.getTaskDetails(taskId));
    if (result.success) {
      setCurrentTask(result.data.data);
    }
    return result;
  }, [execute]);

  const updateTask = useCallback(async (taskId, data) => {
    const result = await execute(() => tasksAPI.updateTask(taskId, data));
    if (result.success) {
      // Update current task if it's the one being updated
      if (currentTask?.task_id === taskId) {
        setCurrentTask(prev => ({ ...prev, ...result.data.data }));
      }
      // Update in tasks list
      setTasks(prev => prev.map(task => 
        task.task_id === taskId ? { ...task, ...result.data.data } : task
      ));
    }
    return result;
  }, [execute, currentTask]);

  const deleteTask = useCallback(async (taskId) => {
    const result = await execute(() => tasksAPI.deleteTask(taskId));
    if (result.success) {
      setTasks(prev => prev.filter(task => task.task_id !== taskId));
      if (currentTask?.task_id === taskId) {
        setCurrentTask(null);
      }
    }
    return result;
  }, [execute, currentTask]);

  const copyTask = useCallback(async (taskId) => {
    const result = await execute(() => tasksAPI.copyTask(taskId));
    return result;
  }, [execute]);

  // Task state management
  const startTask = useCallback(async (taskId, data = {}) => {
    const result = await execute(() => tasksAPI.startTask(taskId, data));
    if (result.success) {
      await fetchTaskById(taskId); // Refresh task details
    }
    return result;
  }, [execute, fetchTaskById]);

  const putTaskOnHold = useCallback(async (taskId, data = {}) => {
    const result = await execute(() => tasksAPI.putTaskOnHold(taskId, data));
    if (result.success) {
      await fetchTaskById(taskId);
    }
    return result;
  }, [execute, fetchTaskById]);

  const resumeTask = useCallback(async (taskId, data = {}) => {
    const result = await execute(() => tasksAPI.resumeTask(taskId, data));
    if (result.success) {
      await fetchTaskById(taskId);
    }
    return result;
  }, [execute, fetchTaskById]);

  const cancelTask = useCallback(async (taskId, data = {}) => {
    const result = await execute(() => tasksAPI.cancelTask(taskId, data));
    if (result.success) {
      await fetchTaskById(taskId);
    }
    return result;
  }, [execute, fetchTaskById]);

  const completeTask = useCallback(async (taskId, data = {}) => {
    const result = await execute(() => tasksAPI.completeTask(taskId, data));
    if (result.success) {
      await fetchTaskById(taskId);
    }
    return result;
  }, [execute, fetchTaskById]);

  const markTaskNotCompleted = useCallback(async (taskId, data = {}) => {
    const result = await execute(() => tasksAPI.markTaskNotCompleted(taskId, data));
    if (result.success) {
      await fetchTaskById(taskId);
    }
    return result;
  }, [execute, fetchTaskById]);

  const reopenTask = useCallback(async (taskId, data = {}) => {
    const result = await execute(() => tasksAPI.reopenTask(taskId, data));
    if (result.success) {
      await fetchTaskById(taskId);
    }
    return result;
  }, [execute, fetchTaskById]);

  const getValidTransitions = useCallback(async (taskId) => {
    return await execute(() => tasksAPI.getValidTransitions(taskId));
  }, [execute]);

  const scheduleNextRecurringTask = useCallback(async (taskId) => {
    const result = await execute(() => tasksAPI.scheduleNextRecurringTask(taskId, {}));
    if (result.success) {
      // Optionally refresh the current task to show updated next_task_id
      if (currentTask?.task_id === taskId) {
        await fetchTaskById(taskId);
      }
    }
    return result;
  }, [execute, currentTask, fetchTaskById]);

  // Search and filtering
  const searchTasks = useCallback(async (params) => {
    return await execute(() => tasksAPI.searchTasks(params));
  }, [execute]);

  const filterTasks = useCallback(async (data) => {
    return await execute(() => tasksAPI.filterTasks(data));
  }, [execute]);

  const getCalendarTasks = useCallback(async (params) => {
    return await execute(() => tasksAPI.getCalendarTasks(params));
  }, [execute]);

  const getOverdueTasks = useCallback(async (params = {}) => {
    return await execute(() => tasksAPI.getOverdueTasks(params));
  }, [execute]);

  // Statistics
  const fetchTaskStatistics = useCallback(async () => {
    const result = await execute(() => tasksAPI.getTaskStatistics());
    if (result.success) {
      setTaskStatistics(result.data.data);
    }
    return result;
  }, [execute]);

  const getCategoryTaskStatistics = useCallback(async (categoryId) => {
    return await execute(() => tasksAPI.getCategoryTaskStatistics(categoryId));
  }, [execute]);

  return {
    tasks,
    currentTask,
    taskStatistics,
    loading,
    error,
    clearError,
    
    // CRUD operations
    createTask,
    fetchDepartmentTasks,
    fetchMyTasks,
    fetchAllMyTasks,
    fetchMyTodaysTasks,
    fetchTaskById,
    updateTask,
    deleteTask,
    copyTask,

    // State management
    startTask,
    putTaskOnHold,
    resumeTask,
    cancelTask,
    completeTask,
    markTaskNotCompleted,
    reopenTask,
    getValidTransitions,
    scheduleNextRecurringTask,

    // Search and filtering
    searchTasks,
    filterTasks,
    getCalendarTasks,
    getOverdueTasks,
    
    // Statistics
    fetchTaskStatistics,
    getCategoryTaskStatistics,
  };
}